import _slicedToArray from "/Users/kevin/Projects/tradingblock/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import Select from 'react-select';
import { isFieldValueFilled } from '../../hooks';
import { FloatingLabel } from '../basic/FloatingLabel';
import { useWindowSizeContext } from '../../context/page/WindowSizeContext'; // Custom filter function that prioritizes prefix matches and improves relevance

var customFilter = function customFilter(option, inputValue) {
  if (!inputValue) return true;
  var input = inputValue.toLowerCase().trim(); // Helper function to safely convert to string

  var safeToString = function safeToString(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();

    if (React.isValidElement(value)) {
      // For JSX elements, try to extract text content or use a fallback
      return '';
    }

    return value ? String(value) : '';
  };

  var label = safeToString(option.label || '').toLowerCase();
  var value = safeToString(option.value || '').toLowerCase();
  var rawValue = safeToString(option.raw_value || '').toLowerCase(); // Check all possible text fields for matches

  var searchableText = "".concat(label, " ").concat(value, " ").concat(rawValue); // Exact prefix match (highest priority) - only match if input is at the start

  if (label.startsWith(input) || value.startsWith(input) || rawValue.startsWith(input)) {
    return true;
  } // Word boundary prefix match (high priority) - only match if input starts a word


  var words = searchableText.split(/\s+/);
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = words[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var word = _step.value;

      if (word.startsWith(input)) {
        return true;
      }
    } // Remove the general contains match - only allow prefix and word boundary matches
    // This prevents "Arizona" from matching when typing "n"

  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return false;
};

export var SelectDropdownInput = function SelectDropdownInput(_ref) {
  var name = _ref.name,
      options = _ref.options,
      value = _ref.value,
      label = _ref.label,
      placeholder = _ref.placeholder,
      error = _ref.error,
      onblur = _ref.onblur,
      onchange = _ref.onchange,
      prefix = _ref.prefix,
      disabled = _ref.disabled,
      controlledLabel = _ref.controlledLabel,
      menuPortalTargetId = _ref.menuPortalTargetId,
      onLabelStateChange = _ref.onLabelStateChange,
      isSearchable = _ref.isSearchable,
      defaultValue = _ref.defaultValue,
      isMulti = _ref.isMulti,
      style = _ref.style,
      filterOption = _ref.filterOption,
      menuPlacement = _ref.menuPlacement,
      _ref$openMenuOnFocus = _ref.openMenuOnFocus,
      openMenuOnFocus = _ref$openMenuOnFocus === void 0 ? true : _ref$openMenuOnFocus,
      onKeyDown = _ref.onKeyDown;

  var _useState = useState(value),
      _useState2 = _slicedToArray(_useState, 2),
      selectValue = _useState2[0],
      setSelectValue = _useState2[1];

  var lastSelectedValueRef = useRef(undefined);
  useEffect(function () {
    setSelectValue(value);
    lastSelectedValueRef.current = value;
  }, [value]);

  var isFilledValue = function isFilledValue(val) {
    return isMulti ? isFieldValueFilled(val && val.length > 0 ? val[0] : null) : isFieldValueFilled(val);
  };

  var _useState3 = useState({
    isFilled: isFilledValue(selectValue)
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      floatingLabelState = _useState4[0],
      setFloatingLabelState = _useState4[1];

  var updateFloatingLabelState = useCallback(function (state) {
    setFloatingLabelState(state);

    if (onLabelStateChange) {
      onLabelStateChange(state);
    }
  }, [onLabelStateChange]);
  useEffect(function () {
    if (!controlledLabel) {
      updateFloatingLabelState({
        isFilled: isFilledValue(selectValue),
        hasFocus: floatingLabelState.hasFocus
      });
    }
  }, [selectValue, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);
  useEffect(function () {
    if (defaultValue) {
      setSelectValue(defaultValue);
    }
  }, []);
  var selectOptions = useMemo(function () {
    return _.map(options, function (o) {
      if (typeof o === 'string') {
        return {
          label: o,
          raw_value: o,
          value: o
        };
      } else {
        return Object.assign({}, o, {
          label: o.label || o.value,
          raw_value: o.raw_value || o.value
        });
      }
    });
  }, [options]);
  var selectValues = useMemo(function () {
    return _.map(_.isArray(selectValue) ? selectValue : !_.isNil(selectValue) ? [selectValue] : [], function (v) {
      // find selected option by matching raw value
      var option = _.find(selectOptions, function (o) {
        return o.raw_value === v;
      });

      return Object.assign({}, option, {
        value: option ? option.value : v,
        label: option ? option.label : v,
        raw_value: option ? option.raw_value : v
      });
    });
  }, [selectValue, selectOptions]);
  var menuPortalTarget = useMemo(function () {
    return menuPortalTargetId && document.getElementById(menuPortalTargetId);
  }, [menuPortalTargetId]);
  var width = useWindowSizeContext().width;
  var narrowPage = useMemo(function () {
    return width < 500;
  }, [width]);
  var smallScreen = width <= 768;
  /* When not searchable, keep inputValue empty so SingleValue stays visible when focused/menu open */

  var isActuallySearchable = !narrowPage && isSearchable;
  var selectInputProps = useMemo(function () {
    if (!isActuallySearchable) {
      return {
        inputValue: '',
        onInputChange: function onInputChange() {}
      };
    }

    return {};
  }, [isActuallySearchable]);
  /* Vertical centering: fixed height + alignItems stretch so value-container fills control;
     react-select's singleValue uses position:absolute + top:50% + translateY(-50%). */

  var controlHeight = smallScreen ? 56 : 48;
  var selectStyles = useMemo(function () {
    return Object.assign({}, menuPortalTargetId && {
      menuPortal: function menuPortal(provided) {
        return Object.assign({}, provided, {
          zIndex: 9999
        });
      }
    }, {
      control: function control(provided) {
        return Object.assign({}, provided, {
          minHeight: controlHeight
        }, !isMulti && {
          height: controlHeight
        }, {
          paddingTop: 0,
          paddingBottom: 0,
          alignItems: 'stretch'
        });
      },
      valueContainer: function valueContainer(provided) {
        return Object.assign({}, provided, {
          height: isMulti ? '100%' : controlHeight,
          alignSelf: 'stretch',
          paddingTop: 0,
          paddingBottom: 0,
          position: 'relative'
        });
      }
    });
  }, [menuPortalTargetId, controlHeight, isMulti]);

  var onFocus = function onFocus(e) {
    updateFloatingLabelState({
      isFilled: true,
      hasFocus: true
    });
  };

  var onBlur = function onBlur(e) {
    if (onblur) {
      onblur(e);
    } // Use lastSelectedValueRef when controlled: onBlur can fire before the value prop updates,
    // and selectValue may still be stale. This prevents the placeholder from reappearing.


    var valueForFilled = value != null ? value : lastSelectedValueRef.current != null ? lastSelectedValueRef.current : selectValue;
    updateFloatingLabelState({
      isFilled: isFilledValue(valueForFilled),
      hasFocus: false
    });
  };

  var onChange = function onChange(o) {
    var values = _.map(_.isArray(o) ? o : [o], function (v) {
      return v.raw_value;
    });

    if (!isMulti) values = _.first(values);

    if (onchange) {
      lastSelectedValueRef.current = values;
      onchange(undefined, values);
    } else {
      setSelectValue(values);
    }

    updateFloatingLabelState({
      isFilled: isFilledValue(values),
      hasFocus: floatingLabelState.hasFocus
    });
  };

  return React.createElement(FloatingLabel, {
    id: name,
    className: error ? 'invalid' : '',
    label: placeholder || label || name,
    prefix: prefix,
    state: floatingLabelState,
    disabled: disabled,
    style: style
  }, React.createElement("div", {
    onKeyDown: onKeyDown,
    style: {
      display: 'contents'
    }
  }, React.createElement(Select, Object.assign({}, isMulti && {
    isMulti: isMulti
  }, selectInputProps, {
    inputId: name,
    name: name,
    className: "basic-multi-select",
    classNamePrefix: "select",
    value: selectValues,
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: onChange,
    options: selectOptions,
    placeholder: "",
    isSearchable: !narrowPage && isSearchable,
    isDisabled: disabled,
    menuPortalTarget: menuPortalTarget,
    styles: selectStyles,
    selectProps: {
      autoComplete: 'nope'
    },
    filterOption: filterOption || (isSearchable ? customFilter : undefined),
    menuPlacement: menuPlacement,
    openMenuOnFocus: openMenuOnFocus,
    controlShouldRenderValue: true
  }))));
};